import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  Cell,
} from "recharts";
import {
  Activity,
  Users,
  MousePointerClick,
  Target,
  Clock,
  Globe,
  LogOut,
  RefreshCw,
  AlertCircle,
  Radio,
  Megaphone,
  Layers,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PINK = "#ff6699";

// UTM values arrive URL-encoded from Facebook (e.g. "12%2F8" = "12/8", "+" = space).
// Decode for display without touching stored data.
const decodeUtm = (v) => {
  if (!v) return v;
  try {
    return decodeURIComponent(String(v).replace(/\+/g, " "));
  } catch {
    return v;
  }
};

const RANGE_OPTIONS = [
  { label: "Last 7 days", value: 7 },
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
];

const StatCard = ({ icon: Icon, label, value, hint, testId }) => (
  <div
    className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-[#ff6699]/30 transition-colors"
    data-testid={testId}
  >
    <div className="flex items-center gap-2 text-white/55 text-xs uppercase tracking-wider">
      <Icon size={14} className="text-[#ff6699]" />
      {label}
    </div>
    <div className="mt-3 font-display font-bold text-3xl text-white tabular-nums">
      {value}
    </div>
    {hint && <div className="mt-1 text-xs text-white/45">{hint}</div>}
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rangeDays, setRangeDays] = useState(30);
  const [customMode, setCustomMode] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [country, setCountry] = useState("");
  const [countryList, setCountryList] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [sources, setSources] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [ads, setAds] = useState(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("fira_admin_token") : null;
  const adminUser = typeof window !== "undefined" ? localStorage.getItem("fira_admin_user") : null;

  useEffect(() => {
    document.title = "Dashboard · Fira Smart Desk";
  }, []);

  // Load country list (once)
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API_BASE}/admin/countries`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        if (!cancelled) setCountryList(data.countries || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, refreshTrigger]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    let cancelled = false;
    const params = {};
    if (customMode && startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    } else {
      params.range_days = rangeDays;
    }
    if (country) params.country = country;

    axios
      .get(`${API_BASE}/admin/stats`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setStats(data);
        setError("");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          localStorage.removeItem("fira_admin_token");
          localStorage.removeItem("fira_admin_user");
          navigate("/admin/login", { replace: true });
          return;
        }
        const msg = err?.response?.data?.detail;
        setError(typeof msg === "string" ? msg : "Failed to load stats");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rangeDays, customMode, startDate, endDate, country, refreshTrigger, token, navigate]);

  // Load attribution (sources + campaigns + ads)
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const params = {};
    if (customMode && startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    } else {
      params.range_days = rangeDays;
    }
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API_BASE}/admin/sources`, { params, headers }),
      axios.get(`${API_BASE}/admin/campaigns`, { params, headers }),
      axios.get(`${API_BASE}/admin/ads`, { params, headers }),
    ])
      .then(([srcRes, campRes, adsRes]) => {
        if (cancelled) return;
        setSources(srcRes.data);
        setCampaigns(campRes.data);
        setAds(adsRes.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [rangeDays, customMode, startDate, endDate, refreshTrigger, token]);

  const onRefresh = () => {
    setRefreshing(true);
    setRefreshTrigger((n) => n + 1);
  };

  const onLogout = () => {
    localStorage.removeItem("fira_admin_token");
    localStorage.removeItem("fira_admin_user");
    navigate("/admin/login", { replace: true });
  };

  const onApplyCustom = () => {
    if (startDate && endDate) {
      setCustomMode(true);
      setRefreshing(true);
      setRefreshTrigger((n) => n + 1);
    }
  };

  const onClearCustom = () => {
    setCustomMode(false);
    setStartDate("");
    setEndDate("");
  };

  const onSelectPreset = (days) => {
    setCustomMode(false);
    setStartDate("");
    setEndDate("");
    setRangeDays(days);
  };

  return (
    <div className="min-h-screen bg-[#08080a] text-white relative overflow-hidden">
      <div
        className="glow-orb"
        style={{
          width: 420,
          height: 420,
          top: "-15%",
          right: "-10%",
          background: "#ff6699",
          opacity: 0.15,
        }}
      />

      {/* Header */}
      <header className="relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">
              Fira Smart Desk
            </div>
            <h1 className="font-display font-bold text-xl tracking-tight">
              Analytics Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-2 text-xs text-white/55">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              {adminUser || "Admin"}
            </span>
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/15 text-xs hover:border-[#ff6699] hover:text-[#ff6699] transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
        {/* Filters */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5 mb-7 space-y-4">
          {/* Preset ranges + refresh */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2" data-testid="range-filter">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onSelectPreset(opt.value)}
                  className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${
                    !customMode && rangeDays === opt.value
                      ? "bg-[#ff6699] text-black border-[#ff6699]"
                      : "border-white/15 text-white/65 hover:border-[#ff6699]/50 hover:text-white"
                  }`}
                  data-testid={`range-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/15 text-xs hover:border-[#ff6699] hover:text-[#ff6699] transition-colors disabled:opacity-50"
              data-testid="refresh-button"
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {/* Custom date range + country */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-white/55 mb-1.5">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || undefined}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#ff6699] outline-none text-sm text-white placeholder-white/30"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-white/55 mb-1.5">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#ff6699] outline-none text-sm text-white placeholder-white/30"
                data-testid="end-date-input"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-white/55 mb-1.5">
                Country
              </label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#ff6699] outline-none text-sm text-white"
                data-testid="country-select"
              >
                <option value="" className="bg-[#0c0c0e]">
                  All countries
                </option>
                {countryList.map((c) => (
                  <option key={c} value={c} className="bg-[#0c0c0e]">
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onApplyCustom}
                disabled={!startDate || !endDate}
                className="flex-1 px-4 py-2 rounded-lg bg-[#ff6699] text-black font-semibold text-xs hover:bg-[#ff7aa6] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="apply-custom-date"
              >
                Apply Date Range
              </button>
              {customMode && (
                <button
                  type="button"
                  onClick={onClearCustom}
                  className="px-3 py-2 rounded-lg border border-white/15 text-xs text-white/65 hover:border-[#ff6699] hover:text-[#ff6699] transition-colors"
                  data-testid="clear-custom-date"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Active filter summary */}
          {stats && (
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/55 pt-2 border-t border-white/5">
              <span className="uppercase tracking-wider">Showing:</span>
              <span className="px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/80">
                {stats.start_date} → {stats.end_date}
              </span>
              {stats.country_filter && (
                <span className="px-2.5 py-1 rounded-full bg-[#ff6699]/15 border border-[#ff6699]/40 text-[#ff6699] font-medium">
                  {stats.country_filter}
                </span>
              )}
            </div>
          )}
        </div>

        {loading && (
          <div className="text-center py-16 text-white/55" data-testid="loading-state">
            Loading…
          </div>
        )}

        {error && !loading && (
          <div
            className="flex items-start gap-2 px-4 py-3 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 text-sm mb-6"
            data-testid="dashboard-error"
          >
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}

        {!loading && stats && (
          <div className="space-y-6">
            {/* Key metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Activity}
                label="Page Views"
                value={stats.page_views.toLocaleString()}
                testId="stat-page-views"
              />
              <StatCard
                icon={Users}
                label="Unique Visitors"
                value={stats.unique_visitors.toLocaleString()}
                testId="stat-unique-visitors"
              />
              <StatCard
                icon={MousePointerClick}
                label="Unique CTA Clicks"
                value={stats.unique_clickers.toLocaleString()}
                testId="stat-cta-clicks"
              />
              <StatCard
                icon={Target}
                label="Conversion Rate"
                value={`${stats.conversion_rate}%`}
                hint="Unique clickers / visitors"
                testId="stat-conversion-rate"
              />
            </div>

            {/* CTA by source */}
            <section
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6"
              data-testid="cta-by-source-section"
            >
              <div className="flex items-center gap-2 mb-5 text-white/65 text-sm font-medium">
                <MousePointerClick size={16} className="text-[#ff6699]" />
                CTA Clicks by Button
              </div>
              {stats.cta_by_source.length === 0 ? (
                <div className="text-white/45 text-sm py-6 text-center">
                  No CTA clicks yet.
                </div>
              ) : (
                <div className="space-y-2.5">
                  {stats.cta_by_source.map((row) => {
                    const max = stats.cta_by_source[0]?.count || 1;
                    const pct = Math.round((row.count / max) * 100);
                    return (
                      <div
                        key={row.source}
                        className="grid grid-cols-[100px_1fr_50px] sm:grid-cols-[140px_1fr_60px] items-center gap-3"
                      >
                        <div className="text-xs sm:text-sm text-white/75 truncate font-medium">
                          {row.source}
                        </div>
                        <div className="relative h-7 rounded-lg bg-white/[0.04] overflow-hidden">
                          <div
                            className="absolute inset-y-0 left-0 rounded-lg bg-[#ff6699]/70"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="text-sm font-semibold tabular-nums text-right">
                          {row.count}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Attribution: Traffic by Source */}
            <section
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6"
              data-testid="traffic-by-source-section"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-white/65 text-sm font-medium">
                  <Radio size={16} className="text-[#ff6699]" />
                  Traffic by Source
                </div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">
                  utm_source
                </div>
              </div>
              {!sources || sources.sources.length === 0 ? (
                <div className="text-white/45 text-sm py-6 text-center">
                  No UTM-tagged traffic yet. Set UTM parameters in your ads to see breakdown here.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-white/40">
                        <th className="pb-3 font-medium">Source</th>
                        <th className="pb-3 font-medium text-right">Visitors</th>
                        <th className="pb-3 font-medium text-right">Clicks</th>
                        <th className="pb-3 font-medium text-right">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sources.sources.map((row) => (
                        <tr
                          key={row.key}
                          className="border-t border-white/5 hover:bg-white/[0.02]"
                          data-testid={`source-row-${row.key}`}
                        >
                          <td className="py-3 text-white/85 font-medium capitalize">
                            {decodeUtm(row.key)}
                          </td>
                          <td className="py-3 text-right tabular-nums text-white/80">
                            {row.unique_visitors.toLocaleString()}
                          </td>
                          <td className="py-3 text-right tabular-nums text-white/80">
                            {(row.unique_clickers ?? 0).toLocaleString()}
                          </td>
                          <td className="py-3 text-right tabular-nums font-semibold text-[#ff6699]">
                            {row.ctr}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Attribution: Placement Performance (FB/IG placement via utm_medium) */}
            <section
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6"
              data-testid="placement-performance-section"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-white/65 text-sm font-medium">
                  <Layers size={16} className="text-[#ff6699]" />
                  Placement Performance
                </div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">
                  utm_medium
                </div>
              </div>
              {!sources || !sources.mediums || sources.mediums.length === 0 ? (
                <div className="text-white/45 text-sm py-6 text-center">
                  No placement data yet. Set{" "}
                  <code className="text-[#ff6699]">
                    utm_medium={"{{placement}}"}
                  </code>{" "}
                  in Facebook ad URL parameters.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-white/40">
                        <th className="pb-3 font-medium">Placement</th>
                        <th className="pb-3 font-medium text-right">Visitors</th>
                        <th className="pb-3 font-medium text-right">Clicks</th>
                        <th className="pb-3 font-medium text-right">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sources.mediums.map((row) => (
                        <tr
                          key={row.key}
                          className="border-t border-white/5 hover:bg-white/[0.02]"
                          data-testid={`placement-row-${row.key}`}
                        >
                          <td className="py-3 text-white/85 font-medium truncate max-w-[280px]">
                            {decodeUtm(row.key)}
                          </td>
                          <td className="py-3 text-right tabular-nums text-white/80">
                            {row.unique_visitors.toLocaleString()}
                          </td>
                          <td className="py-3 text-right tabular-nums text-white/80">
                            {(row.unique_clickers ?? 0).toLocaleString()}
                          </td>
                          <td className="py-3 text-right tabular-nums font-semibold text-[#ff6699]">
                            {row.ctr}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Attribution: Campaign Performance */}
            <section
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6"
              data-testid="campaign-performance-section"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-white/65 text-sm font-medium">
                  <Megaphone size={16} className="text-[#ff6699]" />
                  Campaign Performance
                </div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">
                  utm_campaign
                </div>
              </div>
              {!campaigns || campaigns.campaigns.length === 0 ? (
                <div className="text-white/45 text-sm py-6 text-center">
                  No campaign data yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-white/40">
                        <th className="pb-3 font-medium">Campaign</th>
                        <th className="pb-3 font-medium text-right">Visitors</th>
                        <th className="pb-3 font-medium text-right">Clicks</th>
                        <th className="pb-3 font-medium text-right">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.campaigns.map((row) => (
                        <tr
                          key={row.key}
                          className="border-t border-white/5 hover:bg-white/[0.02]"
                          data-testid={`campaign-row-${row.key}`}
                        >
                          <td className="py-3 text-white/85 font-medium truncate max-w-[220px]">
                            {decodeUtm(row.key)}
                          </td>
                          <td className="py-3 text-right tabular-nums text-white/80">
                            {row.unique_visitors.toLocaleString()}
                          </td>
                          <td className="py-3 text-right tabular-nums text-white/80">
                            {(row.unique_clickers ?? 0).toLocaleString()}
                          </td>
                          <td className="py-3 text-right tabular-nums font-semibold text-[#ff6699]">
                            {row.ctr}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Ads Performance (by utm_content = ad name) */}
            <section
              className="rounded-2xl border border-[#ff6699]/25 bg-white/[0.03] p-5 sm:p-6"
              data-testid="ads-performance-section"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-white/85 text-sm font-medium">
                  <Target size={16} className="text-[#ff6699]" />
                  Ads Performance
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-[#ff6699]/15 border border-[#ff6699]/30 text-[10px] text-[#ff6699] font-semibold tracking-wider uppercase">
                    Ad-Attributed
                  </span>
                </div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">
                  utm_content
                </div>
              </div>
              {!ads || !ads.ads || ads.ads.length === 0 ? (
                <div className="text-white/45 text-sm py-6 text-center">
                  No ad-level data yet. Make sure your Facebook ads include{" "}
                  <code className="text-[#ff6699]">utm_content={"{{ad.name}}"}</code> in URL Parameters.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-white/40">
                        <th className="pb-3 font-medium">Ad Name</th>
                        <th className="pb-3 font-medium text-right">Visitors</th>
                        <th className="pb-3 font-medium text-right">Clicks</th>
                        <th className="pb-3 font-medium text-right">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ads.ads.map((row) => (
                        <tr
                          key={row.key}
                          className="border-t border-white/5 hover:bg-white/[0.02]"
                          data-testid={`ad-row-${row.key}`}
                        >
                          <td className="py-3 text-white/85 font-medium truncate max-w-[280px]">
                            {decodeUtm(row.key)}
                          </td>
                          <td className="py-3 text-right tabular-nums text-white/80">
                            {row.unique_visitors.toLocaleString()}
                          </td>
                          <td className="py-3 text-right tabular-nums text-white/80">
                            {(row.unique_clickers ?? 0).toLocaleString()}
                          </td>
                          <td className="py-3 text-right tabular-nums font-semibold text-[#ff6699]">
                            {row.ctr}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Clicks by hour MYT */}
            <section
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6"
              data-testid="clicks-by-hour-section"
            >
              <div className="flex items-center gap-2 mb-5 text-white/65 text-sm font-medium">
                <Clock size={16} className="text-[#ff6699]" />
                CTA Clicks by Hour (Malaysian Time UTC+8)
              </div>
              <div className="w-full" style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.clicks_by_hour_myt}
                    margin={{ top: 8, right: 8, left: -10, bottom: 0 }}
                  >
                    <XAxis
                      dataKey="hour"
                      stroke="#ffffff55"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(h) => `${String(h).padStart(2, "0")}:00`}
                      interval={2}
                    />
                    <YAxis
                      stroke="#ffffff55"
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                    />
                    <ReTooltip
                      cursor={{ fill: "#ff669915" }}
                      contentStyle={{
                        background: "#0c0c0e",
                        border: "1px solid #ff669940",
                        borderRadius: 12,
                        color: "white",
                      }}
                      labelStyle={{ color: "white" }}
                      itemStyle={{ color: "white" }}
                      labelFormatter={(h) =>
                        `${String(h).padStart(2, "0")}:00 MYT`
                      }
                      formatter={(value) => [`${value} clicks`, "Clicks"]}
                    />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {stats.clicks_by_hour_myt.map((_, i) => (
                        <Cell key={i} fill={PINK} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            {/* Countries */}
            <section
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6"
              data-testid="countries-section"
            >
              <div className="flex items-center gap-2 mb-5 text-white/65 text-sm font-medium">
                <Globe size={16} className="text-[#ff6699]" />
                Visitors by Country
              </div>
              {stats.countries.length === 0 ? (
                <div className="text-white/45 text-sm py-6 text-center">
                  No country data yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {stats.countries.map((row) => {
                    const max = stats.countries[0]?.count || 1;
                    const pct = Math.round((row.count / max) * 100);
                    return (
                      <div
                        key={row.country}
                        className="grid grid-cols-[1fr_60px] items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-sm text-white/85 truncate">
                            {row.country}
                          </span>
                          <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                            <div
                              className="h-full bg-[#ff6699]/70"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                        <div className="text-sm font-semibold tabular-nums text-right">
                          {row.count}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
